import { useEffect, useState } from "react";
import { Link } from "react-router";

import { getExtraction, listDocuments } from "@/api/client";
import type { DocumentListItem, Extraction } from "@/api/client";
import { Notice } from "@/ui/Notice";

interface Unlearned {
  doc: DocumentListItem;
  coded: number;
  measured: number;
}

/** A past take-off whose codes were read but taught no type. */
function unlearned(doc: DocumentListItem, e: Extraction): Unlearned | null {
  const coded = e.stats.coded ?? 0;
  if (e.status !== "done" || coded === 0 || (e.stats.matched ?? 0) > 0) return null;
  return { doc, coded, measured: e.stats.measured ?? 0 };
}

/**
 * Past take-offs that were read but taught nothing, and why, so an empty Types or Score
 * tab does not look as if the upload was ignored. Shows nothing otherwise.
 */
export function NothingLearned() {
  const [rows, setRows] = useState<Unlearned[]>([]);

  useEffect(() => {
    let cancelled = false;
    listDocuments({ kind: "prior_takeoff", limit: 50 })
      .then((page) =>
        Promise.all(
          page.documents.map((doc) =>
            getExtraction(doc.id).then(
              (e) => unlearned(doc, e),
              () => null,
            ),
          ),
        ),
      )
      .then((found) => {
        if (!cancelled) setRows(found.filter((r): r is Unlearned => r !== null));
      })
      .catch(() => {
        // The tab's own empty state still stands; this only explains it.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (rows.length === 0) return null;
  return (
    <Notice tone="warn" className="mt-4">
      <ul className="space-y-1">
        {rows.map(({ doc, coded, measured }) => (
          <li key={doc.id}>
            <Link to={`/knowledge/sources/${doc.id}`}>{doc.filename}</Link>:{" "}
            {coded.toLocaleString()} units read, but{" "}
            {measured === 0
              ? "no panels could be measured on its drawings"
              : "none of its boxes lined up with a measured panel"}
            , so nothing was learned from it yet.
          </li>
        ))}
      </ul>
    </Notice>
  );
}
